import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { fmtEur, fmtDate } from '@/lib/format';

// Register Roboto for Slovak diacritics support (CDN with CORS)
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/roboto/v32/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.ttf' },
      { src: 'https://fonts.gstatic.com/s/roboto/v32/KFOlCnqEu92Fr1MmEU9fBBc4.ttf', fontWeight: 'bold' },
      { src: 'https://fonts.gstatic.com/s/roboto/v32/KFOlCnqEu92Fr1MmSU5fBBc4.ttf', fontStyle: 'italic' },
    ],
  });
} catch (e) { /* ignore re-register errors */ }

const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: 'Roboto', fontSize: 10, color: '#1a1a0f' },
  headRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', borderBottomWidth: 2, borderBottomColor: '#1a1a0f', paddingBottom: 10, marginBottom: 18 },
  smallLabel: { fontSize: 8.5, color: '#6b6545', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1.4, marginBottom: 4 },
  numberBig: { fontSize: 22, fontWeight: 'bold', marginTop: 2 },
  totalBig: { fontSize: 22, fontWeight: 'bold', textAlign: 'right' },
  twoCol: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 18, gap: 24 },
  col: { flex: 1 },
  partyName: { fontSize: 11, fontWeight: 'bold', marginBottom: 3 },
  partyLine: { fontSize: 10, color: '#333', lineHeight: 1.5 },
  datesRow: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#ccc', borderBottomWidth: 1, borderBottomColor: '#ccc', paddingVertical: 8, marginBottom: 16, gap: 14 },
  dateCell: { flex: 1 },
  dateLabel: { fontSize: 8, color: '#6b6545', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 1, marginBottom: 2 },
  dateValue: { fontSize: 10, fontWeight: 'bold' },
  introBox: { fontSize: 10, color: '#444', marginBottom: 14, fontStyle: 'italic' },
  table: { borderWidth: 1, borderColor: '#ddd', marginBottom: 14 },
  tableHead: { flexDirection: 'row', backgroundColor: '#fafaf6', borderBottomWidth: 1, borderBottomColor: '#000', paddingVertical: 6, paddingHorizontal: 6 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#eee', paddingVertical: 7, paddingHorizontal: 6 },
  th: { fontSize: 8.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, color: '#6b6545' },
  td: { fontSize: 10 },
  cellDesc: { flex: 4 },
  cellQty: { flex: 1, textAlign: 'right' },
  cellUnit: { width: 40, textAlign: 'center', color: '#666' },
  cellPrice: { flex: 1.4, textAlign: 'right' },
  cellDph: { width: 50, textAlign: 'center', color: '#666' },
  cellTotal: { flex: 1.6, textAlign: 'right', fontWeight: 'bold' },
  totalsBox: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 16 },
  totalsTable: { width: 230 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  totalLabel: { fontSize: 10, color: '#666' },
  totalValue: { fontSize: 10, fontWeight: 'bold' },
  grandTotalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingTop: 8, borderTopWidth: 1.5, borderTopColor: '#000', marginTop: 4 },
  grandTotalLabel: { fontSize: 11, fontWeight: 'bold' },
  grandTotalValue: { fontSize: 14, fontWeight: 'bold' },
  bankBox: { backgroundColor: '#fafaf6', borderWidth: 1, borderColor: '#ddd', borderRadius: 4, padding: 10, fontSize: 9 },
  bankLabel: { fontSize: 8.5, color: '#6b6545', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 1, marginBottom: 4 },
  bankRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  bankCol: { width: '50%' },
  notice: { marginTop: 18, fontSize: 9, color: '#666', textAlign: 'center' },
});

export default function InvoicePDF({ invoice, workspace, client }) {
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  const isDphPayer = !!workspace?.is_dph_payer;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headRow}>
          <View>
            <Text style={styles.smallLabel}>Faktura</Text>
            <Text style={styles.numberBig}>{invoice?.number || ''}</Text>
          </View>
          <View>
            <Text style={[styles.smallLabel, { textAlign: 'right' }]}>Spolu</Text>
            <Text style={styles.totalBig}>{fmtEur(invoice?.total)}</Text>
            {!isDphPayer && (
              <Text style={{ fontSize: 8.5, color: '#666', textAlign: 'right', marginTop: 4 }}>Nie som platca DPH</Text>
            )}
          </View>
        </View>

        {/* Parties */}
        <View style={styles.twoCol}>
          <View style={styles.col}>
            <Text style={styles.smallLabel}>Dodávateľ</Text>
            <Text style={styles.partyName}>{workspace?.name || ''}</Text>
            <Text style={styles.partyLine}>
              {workspace?.address ? workspace.address + '\n' : ''}
              {workspace?.ico ? 'IČO ' + workspace.ico + '\n' : ''}
              {workspace?.dic ? 'DIČ ' + workspace.dic + '\n' : ''}
              {isDphPayer && workspace?.ic_dph ? 'IČ DPH ' + workspace.ic_dph + '\n' : ''}
              {!isDphPayer ? 'Nie som platca DPH' : ''}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={styles.smallLabel}>Odberateľ</Text>
            <Text style={styles.partyName}>{client?.name || '—'}</Text>
            <Text style={styles.partyLine}>
              {client?.address ? client.address + '\n' : ''}
              {client?.ico ? 'IČO ' + client.ico + '\n' : ''}
              {client?.dic ? 'DIČ ' + client.dic + '\n' : ''}
              {client?.ic_dph ? 'IČ DPH ' + client.ic_dph : ''}
            </Text>
          </View>
        </View>

        {/* Dates */}
        <View style={styles.datesRow}>
          <View style={styles.dateCell}>
            <Text style={styles.dateLabel}>Vystavená</Text>
            <Text style={styles.dateValue}>{fmtDate(invoice?.issue_date)}</Text>
          </View>
          <View style={styles.dateCell}>
            <Text style={styles.dateLabel}>Dodanie</Text>
            <Text style={styles.dateValue}>{fmtDate(invoice?.delivery_date || invoice?.issue_date)}</Text>
          </View>
          <View style={styles.dateCell}>
            <Text style={styles.dateLabel}>Splatnosť</Text>
            <Text style={styles.dateValue}>{fmtDate(invoice?.due_date)}</Text>
          </View>
          <View style={styles.dateCell}>
            <Text style={styles.dateLabel}>VS</Text>
            <Text style={styles.dateValue}>{invoice?.variable_symbol || invoice?.number}</Text>
          </View>
          <View style={styles.dateCell}>
            <Text style={styles.dateLabel}>Spôsob</Text>
            <Text style={styles.dateValue}>{invoice?.payment_method || 'Bankový prevod'}</Text>
          </View>
        </View>

        {/* Intro */}
        {invoice?.intro && <Text style={styles.introBox}>{invoice.intro}</Text>}

        {/* Items */}
        <View style={styles.table}>
          <View style={styles.tableHead}>
            <Text style={[styles.th, styles.cellDesc]}>Popis</Text>
            <Text style={[styles.th, styles.cellQty]}>Mn.</Text>
            <Text style={[styles.th, styles.cellUnit]}>MJ</Text>
            <Text style={[styles.th, styles.cellPrice]}>Cena</Text>
            {isDphPayer && <Text style={[styles.th, styles.cellDph]}>DPH</Text>}
            <Text style={[styles.th, styles.cellTotal]}>Spolu</Text>
          </View>
          {items.map((it, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={[styles.td, styles.cellDesc]}>{it.desc || ''}</Text>
              <Text style={[styles.td, styles.cellQty]}>{it.qty}</Text>
              <Text style={[styles.td, styles.cellUnit]}>{it.unit || 'ks'}</Text>
              <Text style={[styles.td, styles.cellPrice]}>{fmtEur(it.price)}</Text>
              {isDphPayer && <Text style={[styles.td, styles.cellDph]}>{it.dph_rate || 0}%</Text>}
              <Text style={[styles.td, styles.cellTotal]}>
                {fmtEur((Number(it.qty) || 0) * (Number(it.price) || 0))}
              </Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsBox}>
          <View style={styles.totalsTable}>
            {isDphPayer ? (
              <>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Suma bez DPH</Text>
                  <Text style={styles.totalValue}>{fmtEur(invoice?.subtotal)}</Text>
                </View>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>DPH</Text>
                  <Text style={styles.totalValue}>{fmtEur(invoice?.dph_amount)}</Text>
                </View>
              </>
            ) : (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Suma</Text>
                <Text style={styles.totalValue}>{fmtEur(invoice?.subtotal || invoice?.total)}</Text>
              </View>
            )}
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>K úhrade</Text>
              <Text style={styles.grandTotalValue}>{fmtEur(invoice?.total)}</Text>
            </View>
          </View>
        </View>

        {/* Bank box */}
        <View style={styles.bankBox}>
          <Text style={styles.bankLabel}>Bankové spojenie</Text>
          <View style={styles.bankRow}>
            <Text style={styles.bankCol}>IBAN  {workspace?.iban || ''}</Text>
            <Text style={styles.bankCol}>VS  {invoice?.variable_symbol || invoice?.number}</Text>
          </View>
          <View style={styles.bankRow}>
            {workspace?.bank ? <Text style={styles.bankCol}>Banka  {workspace.bank}</Text> : <Text style={styles.bankCol}> </Text>}
            {workspace?.bic ? <Text style={styles.bankCol}>BIC  {workspace.bic}</Text> : <Text style={styles.bankCol}> </Text>}
          </View>
        </View>

        <Text style={styles.notice}>
          {workspace?.notice || (isDphPayer
            ? 'Všetky sumy sú s DPH.'
            : 'Nie som platca DPH — oslobodenie podľa §4 ods. 1 zák. č. 222/2004 Z. z.')}
        </Text>
      </Page>
    </Document>
  );
}
